import { observer, useLocalObservable } from 'mobx-react';
import * as Portal from '@radix-ui/react-portal';
import IconButton, { ICON_BUTTON_TYPES } from '../../../components/buttons/IconButton';
import { useStore } from '../../../store/store';
import { useEffect } from 'react';
import { runInAction } from 'mobx';
import {
  ADMIN_DASHBOARD_USER_GROUPS_ARRAY_ALL,
  ADMIN_DASHBOARD_USER_GROUPS_ARRAY_SELECTABLE
} from '../constants';
import { FIELDS_TYPES } from '../../../utils/constants/fields';
import { HEADER_MESSAGE_TYPES } from '../../../utils/constants/header';

const SelectUserGroupsPopup = observer(({ user = {}, onClose = () => {} }) => {
  const { adminDashboardStore, utilsStore, authStore } = useStore();
  const state = useLocalObservable(() => ({
    isRendered: true,
    cognitoGroups: [],
    get currentUserGroups() {
      return user.cognitoGroups;
    },
    get hasGroupChanges() {
      const changeCheck1 = state.cognitoGroups.length !== state.currentUserGroups.length;
      const changeCheck2 = !!state.cognitoGroups.find((g) => !state.currentUserGroups.includes(g));
      const changeCheck3 = !!state.currentUserGroups.find((g) => !state.cognitoGroups.includes(g));
      return changeCheck1 || changeCheck2 || changeCheck3;
    },
    get isReadingLiveState() {
      return adminDashboardStore.isRefreshingUsersManagementUser;
    },
    get liveStateError() {
      return adminDashboardStore.refreshUsersManagementUserError;
    },
    // TD-2361: seed from what the backend holds right now, not from the row cached
    // when the list was last loaded — otherwise a change made by another admin (or
    // another tab) is invisible here and the next save silently re-grants it.
    // Called on open and from Retry.
    readLiveState: () => {
      adminDashboardStore.refreshUsersManagementUser(user.id, {
        onMissing: () => {
          // The popup may already be gone — the admin can cancel while the read is
          // in flight. A toast plus a forced list reload about an editor that no
          // longer exists is noise, not recovery.
          if (!state.isRendered) {
            return;
          }
          onClose();
          utilsStore.setHeaderMessage(
            `${user.firstName} ${user.lastName} is no longer in this view. Reloading the list.`,
            HEADER_MESSAGE_TYPES.ERROR,
            8_000
          );
          adminDashboardStore.loadUsersManagementList(adminDashboardStore.usersManagementPage);
        }
      });
    },
    // Cancel stays available throughout: the admin must always be able to back out,
    // including out of a failed read.
    get disableCancel() {
      return adminDashboardStore.isUpdatingUser;
    },
    get disableSave() {
      return (
        adminDashboardStore.isUpdatingUser ||
        !state.hasGroupChanges ||
        state.isReadingLiveState ||
        !!state.liveStateError
      );
    },
    get disableInputs() {
      return (
        adminDashboardStore.isUpdatingUser || state.isReadingLiveState || !!state.liveStateError
      );
    },
    showDestructiveConfirm: false,
    get hasDestructiveRemoval() {
      // Any group flagged destructiveOnRemove that's in the prior set
      // but NOT in the next set. Read via the constants table.
      return ADMIN_DASHBOARD_USER_GROUPS_ARRAY_ALL.some(
        (g) =>
          g.destructiveOnRemove &&
          state.currentUserGroups.includes(g.value) &&
          !state.cognitoGroups.includes(g.value)
      );
    },
    onSave: () => {
      if (state.hasDestructiveRemoval) {
        runInAction(() => {
          state.showDestructiveConfirm = true;
        });
        return;
      }
      state.confirmDestructiveSave();
    },
    confirmDestructiveSave: () => {
      const userDisplayName = user.firstName + ' ' + user.lastName;
      const wasDestructive = state.hasDestructiveRemoval;
      adminDashboardStore.updateUserGroups(
        user,
        state.cognitoGroups,
        () => {
          if (state.isRendered) {
            onClose();
          }
          if (user.id === authStore.currentUserId) {
            utilsStore.setHeaderMessage(
              `Changes have been SAVED for CURRENT SESSION. Please RELOGIN to view them. Or refresh.`,
              HEADER_MESSAGE_TYPES.SUCCESS,
              10_000
            );
          } else if (wasDestructive) {
            utilsStore.setHeaderMessage(
              `MFA cleared for ${userDisplayName}. They have been signed out of all sessions.`,
              HEADER_MESSAGE_TYPES.SUCCESS,
              10_000
            );
          } else {
            utilsStore.setHeaderMessage(
              `Changes to group were successfully saved for ${userDisplayName}.`
            );
          }
        },
        (errorMessage) => {
          if (state.isRendered) {
            onClose();
          }
          utilsStore.setHeaderMessage(
            errorMessage || `Failed to save group changes for ${userDisplayName}.`,
            HEADER_MESSAGE_TYPES.ERROR,
            8_000
          );
        }
      );
    },
    cancelDestructiveConfirm: () => {
      runInAction(() => {
        state.showDestructiveConfirm = false;
      });
    }
  }));

  useEffect(() => {
    return () => {
      runInAction(() => {
        state.isRendered = false;
      });
      adminDashboardStore.clearRefreshUsersManagementUserError();
    };
  }, [state, adminDashboardStore]);

  // Open-once. The refresh mutates the existing row in place rather than replacing
  // it, so it can't remount this popup and re-enter here.
  useEffect(() => {
    state.readLiveState();
  }, [state]);

  // Re-seeds when the refresh above lands a new cognitoGroups array on the row.
  useEffect(() => {
    runInAction(() => {
      state.cognitoGroups = user.cognitoGroups.slice();
    });
  }, [user.cognitoGroups]);

  return (
    <Portal.Root>
      <div className="admin-dashboard-select-user-groups-popup-closer" onClick={onClose} />
      {state.showDestructiveConfirm ? (
        <div className="admin-dashboard-select-user-groups-popup">
          <div className="title">
            Disable MFA for {user.firstName} {user.lastName}?
          </div>
          <div className="subtitle">
            This will:
            <ul>
              <li>clear their authenticator app setup</li>
              <li>sign them out of all active sessions</li>
            </ul>
            Your own session is not affected.
          </div>
          <div className="actions">
            <IconButton
              innerText="Cancel"
              onClick={state.cancelDestructiveConfirm}
              disabled={state.disableInputs}
            />
            <IconButton
              type={ICON_BUTTON_TYPES.BLUE}
              filled
              innerText="Confirm"
              onClick={state.confirmDestructiveSave}
              disabled={state.disableInputs}
            />
          </div>
        </div>
      ) : (
        <div className="admin-dashboard-select-user-groups-popup">
          <div>
            <div className="title">
              Select user group(s) for {user.firstName} {user.lastName}
            </div>
            <div className="subtitle">
              Manage group membership. Groups can be used to add permissions to the access token for
              multiple users.
            </div>
          </div>
          <div className="selections-title">
            <div className="title">Group name</div>
            <div className="title long">Description</div>
          </div>
          <div className="selections">
            {ADMIN_DASHBOARD_USER_GROUPS_ARRAY_SELECTABLE.filter(
              ({ value }) =>
                !(!state.currentUserGroups.includes('investor') && [].includes(value)) &&
                !(!state.currentUserGroups.includes('agent') && [].includes(value))
            ).map((group) => {
              const checked = state.cognitoGroups.includes(group.value);
              const isLinkedInBlocked = !!user.isLinkedInFederated && group.value === 'mfa_required';
              const isDisabled = state.disableInputs || isLinkedInBlocked;
              const linkedInTitle = isLinkedInBlocked
                ? 'Not applicable — LinkedIn-federated users bypass Cognito MFA'
                : undefined;
              return (
                <div key={group.value} className="row">
                  <div className="name">
                    <div className="noselect options-layout">
                      <label>
                        <input
                          type={FIELDS_TYPES.TYPE_CHECKBOX}
                          checked={checked}
                          onChange={() => {
                            if (isLinkedInBlocked) {
                              return;
                            }
                            runInAction(() => {
                              if (checked) {
                                state.cognitoGroups = state.cognitoGroups.filter(
                                  (g) => g !== group.value
                                );
                              } else {
                                if (group.profilerGroup) {
                                  const cognitoGroups = ADMIN_DASHBOARD_USER_GROUPS_ARRAY_ALL.filter(
                                    (g) => g.profilerGroup
                                  ).map((g) => g.value);
                                  state.cognitoGroups = state.cognitoGroups.filter(
                                    (val) => !cognitoGroups.includes(val)
                                  );
                                }
                                state.cognitoGroups.push(group.value);
                              }
                            });
                          }}
                          disabled={isDisabled}
                          title={linkedInTitle}
                        />
                        {group.name}
                      </label>
                    </div>
                  </div>
                  <div className="description">{group.description}</div>
                </div>
              );
            })}
          </div>
          {state.isReadingLiveState && (
            <div className="live-state-status" role="status" aria-live="polite">
              Reading current access…
            </div>
          )}
          {!!state.liveStateError && (
            <div className="live-state-status error" role="alert">
              <span>{state.liveStateError}</span>
              <IconButton innerText="Retry" onClick={state.readLiveState} />
            </div>
          )}
          <div className="actions">
            <IconButton innerText="Cancel" onClick={onClose} disabled={state.disableCancel} />
            <IconButton
              type={ICON_BUTTON_TYPES.BLUE}
              filled
              innerText="Save"
              disabled={state.disableSave}
              onClick={state.onSave}
            />
          </div>
        </div>
      )}
    </Portal.Root>
  );
});

export default SelectUserGroupsPopup;
